/**
 * Anon→login race replay loop (spec §6.2 multi-slot drafts).
 *
 * A guest can hold SEVERAL pending races (goal + prep race + a second goal —
 * see `anonDraft.ts`). On the anon→customer transition each one is replayed as
 * a real `CustomerEvent` via the standard events API, then — when the draft
 * carries the applied plan's `planGroupId` (or an anon `build` `journeyId`) —
 * linked onto the merged workouts/journey so the calendar banner names the
 * race. Extracted from `App.tsx`'s `AnonRaceReplay` so the loop is unit-
 * testable with mocked API calls.
 *
 * Failure semantics (matches the historical single-slot behavior, where any
 * error kept the draft for a retry on the next mount): entries are replayed
 * in insertion order, each removed from storage as it succeeds; the FIRST
 * failure stops the loop, keeping the failed entry AND the remainder in
 * storage so the next login/mount retries the whole tail. Linking is
 * idempotent server-side, so a retried entry is safe even if its create
 * already landed (the create is server-deduped too).
 */

import { getAnonPendingRaceEvents, removeAnonPendingRaceEvent } from "./anonDraft";
import type { CreateEventInput, CreateEventOutcome } from "./eventsApi";

export type AnonRaceReplayDeps = {
  /** `useEventMutations().create` — CSRF-guarded + server-deduped. */
  createEvent: (input: CreateEventInput) => Promise<CreateEventOutcome>;
  /** `linkTrainingPlanEvent` with csrf plumbing applied; resolves the linked count. */
  linkEvent: (target: {
    planGroupId?: string;
    journeyId?: string;
    customerEventId: string;
    intent?: "goal" | "milestone";
  }) => Promise<number>;
};

export type AnonRaceReplayResult = {
  /** Entries fully replayed (created [+ linked]) and removed from storage. */
  replayed: number;
  /** Total workout/journey rows linked across all entries (0 ⇒ nothing to refresh). */
  linked: number;
  /** True ⇒ an entry errored; it and the remainder stay in storage for a retry. */
  failed: boolean;
};

/**
 * Replay every held race draft in insertion order. Never rejects — a failure
 * is reported via `result.failed` (with the failed entry + tail left in
 * storage) so the caller can re-arm its retry guard.
 */
export async function replayAnonPendingRaceEvents(
  deps: AnonRaceReplayDeps
): Promise<AnonRaceReplayResult> {
  const entries = getAnonPendingRaceEvents();
  let replayed = 0;
  let linked = 0;
  for (const pending of entries) {
    // Carry the guest's chosen J4 intent (goal vs milestone) onto the replayed
    // event, MARKED as a replay. The mark is what stops the create's dedupe
    // branch from re-stamping intent on a row that already exists: this draft
    // may be days old and the server-side event may since have become a GOAL
    // with a journey built on it, so replaying `milestone` over it would break
    // that linkage on the first signed-in page load. A NEW event still gets the
    // draft's intent. (Role changes after the fact go through the reclassify
    // endpoint, which re-validates containment.)
    const input: CreateEventInput = pending.eventCatalogId
      ? {
          eventCatalogId: pending.eventCatalogId,
          distanceLabel: pending.distanceLabel,
          intent: pending.intent,
          fromReplay: true
        }
      : {
          name: pending.name,
          eventDate: pending.dateYmd,
          intent: pending.intent,
          fromReplay: true
        };
    try {
      const outcome = await deps.createEvent(input);
      // A 0 link result is terminal — already linked, or the plan was removed
      // before login — so the entry is still cleared (a retry can't change it).
      // Drafts written before `planGroupId`/`journeyId` existed skip linking;
      // their event is still created, only the race name won't auto-backfill.
      if (pending.planGroupId || pending.journeyId) {
        linked += await deps.linkEvent({
          planGroupId: pending.planGroupId,
          journeyId: pending.journeyId,
          customerEventId: outcome.event.id,
          intent: pending.intent
        });
      }
      removeAnonPendingRaceEvent(pending);
      replayed += 1;
    } catch {
      // Event-create or link errored — keep this entry + the remainder and
      // stop; the next mount retries the tail in order.
      return { replayed, linked, failed: true };
    }
  }
  return { replayed, linked, failed: false };
}
